import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@app/env';
import { finalize } from 'rxjs';
import { AuthStore } from '../store';

export interface TokenValidationResponse {
  token: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  status: 'PENDING' | 'ACCOUNT_CREATED';
  expiresAt: string;
  usedAt: string;
}

export type MagicLinkRegistrationPayload = Pick<
  TokenValidationResponse,
  'firstName' | 'lastName' | 'email' | 'employeeId' | 'token'
>;

export interface CodeVerificationPayload {
  otp: string;
  email: string;
  session: string;
}

export interface CodeVerificationResponse {
  username: string;
  session: string;
  authStatus: 'SUCCESS' | 'FORCE_PASSWORD_RESET';
  skey: string;
}

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  readonly #http = inject(HttpClient);
  readonly #authStore = inject(AuthStore);
  readonly PUBLIC_USERS_API_BASE_URL = `${environment.publicApiBaseUrl}/users`;
  readonly PRIVATE_USERS_API_BASE_URL = `${environment.apiBaseUrl}/users`;

  login(email: string, password: string) {
    return this.#http.post<{
      authStatus: string;
      session: null;
      skey: string;
      username: string;
    }>(`${this.PUBLIC_USERS_API_BASE_URL}/login`, {
      email,
      password,
    });
  }

  registerWithMagicLink(magicLinkRegistrationPayload: MagicLinkRegistrationPayload) {
    return this.#http.post<{ message: string }>(
      `${this.PUBLIC_USERS_API_BASE_URL}/register/magic-link`,
      magicLinkRegistrationPayload,
    );
  }

  loginWithMagicLink(email: string) {
    return this.#http.post<{
      session: string;
    }>(`${this.PUBLIC_USERS_API_BASE_URL}/login/magic-link`, { email });
  }

  verifyCode(payload: CodeVerificationPayload) {
    return this.#http.post<CodeVerificationResponse>(
      `${this.PUBLIC_USERS_API_BASE_URL}/login/magic-link/verify`,
      payload,
    );
  }

  logout() {
    return this.#http
      .post<void>(`${this.PRIVATE_USERS_API_BASE_URL}/logout`, {})
      .pipe(finalize(() => this.#authStore.logout()));
  }

  resetPassword(email: string) {
    return this.#http.post<void>(`${this.PUBLIC_USERS_API_BASE_URL}/resetPassword`, { email });
  }

  confirmResetPassword(email: string, confirmationCode: string, newPassword: string) {
    return this.#http.post<void>(`${this.PUBLIC_USERS_API_BASE_URL}/confirmResetPassword`, { email, confirmationCode, newPassword });
  }
}
